import { useS3Upload } from "@src/features/app/hooks/useS3Upload";
import {
  createDefaultImageReader,
  createDefaultImageWriter,
  createDefaultShapePreprocessor,
  locale_en_gb,
  markup_editor_defaults,
  markup_editor_locale_en_gb,
  plugin_annotate_locale_en_gb,
  plugin_crop_locale_en_gb,
} from "@pqina/pintura";
import "@pqina/pintura/pintura.css";
import "@src/lib/ui/pinturaSetup";
import { PinturaEditorModal } from "@pqina/react-pintura";
import { FC } from "react";

const editorDefaults = {
  imageReader: createDefaultImageReader(),
  imageWriter: createDefaultImageWriter(),
  shapePreprocessor: createDefaultShapePreprocessor(),
  // Limit the toolbar to crop + annotate. plugin_trim is registered
  // globally (so the video modal can use it) but we don't want it on
  // images, and finetune/filter aren't part of the photo editor scope.
  utils: ["crop", "annotate"],
  ...markup_editor_defaults,
  locale: {
    ...locale_en_gb,
    ...plugin_crop_locale_en_gb,
    ...plugin_annotate_locale_en_gb,
    ...markup_editor_locale_en_gb,
  },
};

type BaseProps = {
  imageUrl: string;
  visible: boolean;
  hide: () => void;
  aspectRatio?: number;
  lockAspectRatio?: boolean;
};

type WithNewImageFile = {
  onNewImageFile: (file: File) => void;
  onNewImageUrl?: never;
};

type WithNewImageUrl = {
  onNewImageFile?: never;
  onNewImageUrl: (imageUrl: string) => void;
};

type Props = BaseProps & (WithNewImageFile | WithNewImageUrl);

const PinturaModal: FC<Props> = ({
  imageUrl,
  onNewImageFile,
  onNewImageUrl,
  visible,
  hide,
  aspectRatio,
  lockAspectRatio,
}) => {
  const { handleFileUpload } = useS3Upload({
    onUploadComplete: (upload) => {
      onNewImageUrl ? onNewImageUrl(upload.url) : undefined;
    },
  });

  if (!visible) {
    return null;
  }
  return (
    <PinturaEditorModal
      src={imageUrl}
      {...editorDefaults}
      imageCropAspectRatio={aspectRatio}
      cropEnableSelectPreset={!lockAspectRatio}
      className="pintura"
      onHide={hide}
      onProcess={({ dest }) => {
        onNewImageFile ? onNewImageFile(dest) : undefined;
        if (onNewImageUrl) {
          handleFileUpload(dest);
        }
      }}
    />
  );
};

export default PinturaModal;
